import { MesfModal } from "@dexteel/mesf-core";
import { useDebouncedValue } from "@dexteel/mesf-core/dist/pages/trendings-v2/components/chart/hooks/useDebouncedValue";
import {
	Autocomplete,
	Box,
	FormHelperText,
	Grid2,
	InputAdornment,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { type HTMLAttributes, type Key, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { FooterModalButtons } from "@/controls/buttons/FooterModalButtons";
import { capsSx } from "@/pages/sawlines/components/primitives";
import { getButtonSx } from "@/shared/selectors/AreaButtonGroup";
import {
	getMillHeatGrade,
	type MillBundleSource,
	type MillHeat,
	type MillSection,
	type NewMillBundle,
	type TagStationHeader,
} from "../repositories/tagStationRepository";
import {
	carryInches,
	type Draft,
	type Field,
	invalid,
	sectionLabel,
	SOURCES,
	valuesOf,
} from "../tagStation";

interface Form extends Draft {
	where: NewMillBundle["where"];
	source: MillBundleSource;
	sourceNote: string;
}

// two inputs that read as one field: shared border in the middle
const joinedSx = {
	display: "flex",
	"& .MuiTextField-root": { flex: 1, minWidth: 0 },
	"& .MuiTextField-root:first-of-type fieldset": {
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},
	"& .MuiTextField-root:last-of-type": { ml: "-1px" },
	"& .MuiTextField-root:last-of-type fieldset": {
		borderTopLeftRadius: 0,
		borderBottomLeftRadius: 0,
	},
	// hovered / focused half on top, so its whole border shows
	"& .MuiOutlinedInput-root:hover, & .MuiOutlinedInput-root.Mui-focused": {
		zIndex: 1,
	},
} as const;

// a heat in a list, its grade at the right
export const heatOption =
	(heats: MillHeat[]) =>
	(
		{ key, ...props }: HTMLAttributes<HTMLLIElement> & { key: Key },
		heat: string,
	) => {
		const grade = heats.find((h) => h.HeatNo === heat)?.GradeName;
		return (
			<Box component="li" key={key} {...props}>
				{heat}
				<Box sx={{ ml: "auto", color: "text.secondary", fontSize: 13 }}>
					{grade ? `Gr ${grade}` : ""}
				</Box>
			</Box>
		);
	};

const unit = (text: string) => ({
	endAdornment: <InputAdornment position="end">{text}</InputAdornment>,
});

export const NewBundleModal = ({
	header,
	sections,
	grades,
	heats,
	heatsLoading,
	comments,
	sourceNotes,
	saving,
	onClose,
}: {
	header?: TagStationHeader;
	sections: MillSection[];
	grades: string[];
	heats: MillHeat[];
	heatsLoading: boolean;
	comments: string[];
	sourceNotes: string[];
	saving: boolean;
	onClose: (bundle: NewMillBundle | null) => void;
}) => {
	const last = header?.LastLengthIn ?? null;
	const {
		control,
		handleSubmit,
		watch,
		setValue,
		getValues,
		formState: { errors },
	} = useForm<Form>({
		defaultValues: {
			where: "Station",
			source: "Danieli",
			sourceNote: "",
			section: header?.RollingSection ?? "",
			// read off the bars, never assumed
			heat: "",
			grade: "",
			ft: last == null ? "" : String(Math.floor(last / 12)),
			inch: last == null ? "" : String(last % 12),
			pcs: header?.LastPieces == null ? "" : String(header.LastPieces),
			comment: "",
		},
	});

	const typed = watch("heat").trim();
	const gradeOf = useMemo(
		() => new Map(heats.map((h) => [h.HeatNo, h.GradeName])),
		[heats],
	);
	// hint, lookup and grade wait for a pause in the typing; a listed heat settles at once
	const settled = useDebouncedValue(typed, 400);
	const heat = gradeOf.has(typed) ? typed : settled;
	// undefined = not in the list: the mill may still know it
	const listed = gradeOf.get(heat);
	const lookup = useQuery({
		queryKey: ["millHeatGrade", heat],
		queryFn: () => getMillHeatGrade(heat),
		enabled: heat !== "" && listed === undefined,
		staleTime: Number.POSITIVE_INFINITY,
		retry: false,
	});
	const heatGrade = listed ?? lookup.data ?? null;
	// the grade belongs to the heat: another heat never keeps the previous one's
	useEffect(() => {
		setValue("grade", heatGrade ?? "");
	}, [heat, heatGrade, setValue]);
	const heatHint = !heat
		? "Required"
		: heatGrade
			? `Grade ${heatGrade} comes with this heat`
			: listed === null
				? "Pick the grade."
				: lookup.isFetched
					? "New heat: it stays in the list from now on. Pick its grade."
					: " ";

	// same rules and messages as the checklist
	const check = (f: Field) => invalid(valuesOf(getValues()), f) ?? true;
	const other = watch("source") === "Other";
	const lengthError = errors.ft?.message ?? errors.inch?.message;
	const carry = () => {
		const { ft, inch } = carryInches(getValues("ft"), getValues("inch"));
		setValue("ft", ft, { shouldValidate: true });
		setValue("inch", inch, { shouldValidate: true });
	};

	return (
		<MesfModal
			title="NEW BUNDLE"
			open
			maxWidth="md"
			handleClose={() => onClose(null)}
		>
			<form
				onSubmit={handleSubmit(({ where, source, sourceNote, ...draft }) =>
					onClose({
						where,
						source,
						sourceNote: source === "Other" ? sourceNote.trim() : "",
						values: valuesOf(draft),
						comment: draft.comment.trim(),
					}),
				)}
			>
				<MesfModal.Content
					style={{ padding: "20px 30px", width: 720, boxSizing: "border-box" }}
				>
					<Grid2 container spacing={2}>
						<Grid2 size={12}>
							<Typography sx={{ ...capsSx, mb: 0.75 }}>Where is it</Typography>
							<Controller
								name="where"
								control={control}
								render={({ field }) => (
									<ToggleButtonGroup
										exclusive
										fullWidth
										size="small"
										value={field.value}
										onChange={(_, value) => value && field.onChange(value)}
									>
										<ToggleButton
											value="Station"
											sx={getButtonSx("primary.main")}
										>
											At the station
										</ToggleButton>
										<ToggleButton
											value="Aside"
											sx={getButtonSx("primary.main")}
										>
											Set aside
										</ToggleButton>
									</ToggleButtonGroup>
								)}
							/>
						</Grid2>
						<Grid2 size={6}>
							<Controller
								name="source"
								control={control}
								render={({ field }) => (
									<TextField
										{...field}
										select
										fullWidth
										size="small"
										label="Came from"
										helperText=" "
									>
										{Object.entries(SOURCES).map(([value, label]) => (
											<MenuItem key={value} value={value}>
												{label}
												{value === "Transfer" && " (scrap)"}
											</MenuItem>
										))}
									</TextField>
								)}
							/>
						</Grid2>
						<Grid2 size={6}>
							<Controller
								name="section"
								control={control}
								rules={{ required: "Section is required" }}
								render={({ field, fieldState: { error } }) => (
									<Autocomplete
										size="small"
										options={sections.map((s) => s.SectionName)}
										getOptionLabel={(name) => sectionLabel(name, sections)}
										value={field.value || null}
										onChange={(_, value) => field.onChange(value ?? "")}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Section"
												error={!!error}
												helperText={
													error?.message ??
													(field.value && field.value === header?.RollingSection
														? "Rolling now on the 2 Mill"
														: " ")
												}
											/>
										)}
									/>
								)}
							/>
						</Grid2>
						{other && (
							<Grid2 size={12}>
								<Controller
									name="sourceNote"
									control={control}
									rules={{
										validate: (value) =>
											value.trim() !== "" || "Say where the bundle came from",
									}}
									render={({ field, fieldState: { error } }) => (
										<Autocomplete
											freeSolo
											size="small"
											options={sourceNotes}
											inputValue={field.value}
											onInputChange={(_, value) => field.onChange(value)}
											renderInput={(params) => (
												<TextField
													{...params}
													autoFocus
													label="Where from *"
													placeholder="Type it or pick one used before"
													error={!!error}
													helperText={error?.message ?? " "}
													inputProps={{ ...params.inputProps, maxLength: 50 }}
												/>
											)}
										/>
									)}
								/>
							</Grid2>
						)}
						<Grid2 size={6}>
							<Controller
								name="heat"
								control={control}
								rules={{ validate: () => check("heat") }}
								render={({ field, fieldState: { error } }) => (
									<Autocomplete
										freeSolo
										// arrow shows it is a list; it opens on click or typing
										forcePopupIcon
										loading={heatsLoading}
										size="small"
										options={heats.map((h) => h.HeatNo)}
										inputValue={field.value}
										onInputChange={(_, value) => field.onChange(value)}
										renderOption={heatOption(heats)}
										renderInput={(params) => (
											<TextField
												{...params}
												autoFocus
												label="Heat *"
												placeholder="Pick it or type it off the bars"
												error={!!error}
												helperText={error?.message ?? heatHint}
												inputProps={{ ...params.inputProps, maxLength: 20 }}
											/>
										)}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={6}>
							<Controller
								name="grade"
								control={control}
								render={({ field }) => (
									<Autocomplete
										size="small"
										// the heat's grade stays pickable even if the catalog dropped it
										options={
											!field.value || grades.includes(field.value)
												? grades
												: [field.value, ...grades]
										}
										value={field.value || null}
										onChange={(_, value) => field.onChange(value ?? "")}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Grade"
												helperText={
													heatGrade && field.value === heatGrade
														? "Comes with the heat"
														: " "
												}
											/>
										)}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={6}>
							<Box sx={joinedSx}>
								<Controller
									name="ft"
									control={control}
									rules={{ validate: () => check("len") }}
									render={({ field }) => (
										<TextField
											{...field}
											size="small"
											type="number"
											label="Length"
											error={!!lengthError}
											InputProps={unit("ft")}
										/>
									)}
								/>
								<Controller
									name="inch"
									control={control}
									rules={{ validate: () => check("len") }}
									render={({ field }) => (
										<TextField
											{...field}
											onBlur={() => {
												field.onBlur();
												carry();
											}}
											size="small"
											type="number"
											error={!!lengthError}
											inputProps={{ "aria-label": "Length inches" }}
											InputProps={unit("in")}
										/>
									)}
								/>
							</Box>
							<FormHelperText error={!!lengthError} sx={{ mx: "14px" }}>
								{lengthError ??
									(last != null ? "Same as the last bundle" : " ")}
							</FormHelperText>
						</Grid2>
						<Grid2 size={6}>
							<Controller
								name="pcs"
								control={control}
								rules={{ validate: () => check("pcs") }}
								render={({ field, fieldState: { error } }) => (
									<TextField
										{...field}
										fullWidth
										size="small"
										type="number"
										label="Pieces"
										error={!!error}
										helperText={
											error?.message ??
											(header?.LastPieces != null
												? "Same as the last bundle"
												: " ")
										}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={12}>
							<Controller
								name="comment"
								control={control}
								render={({ field }) => (
									<Autocomplete
										freeSolo
										size="small"
										options={comments}
										inputValue={field.value}
										onInputChange={(_, value) => field.onChange(value)}
										renderInput={(params) => (
											<TextField
												{...params}
												label="Comment"
												inputProps={{ ...params.inputProps, maxLength: 200 }}
											/>
										)}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
					<FooterModalButtons
						isSubmitLoading={saving}
						onHide={() => onClose(null)}
					/>
				</MesfModal.Actions>
			</form>
		</MesfModal>
	);
};
